import { styled } from "@mui/material/styles";
import { Meta, PageTitle, StepsBar } from "@UI";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { WorkflowWrapper } from "../WorkflowWrapper";
import { FormattedDocumentFetcher } from "./FormattedDocumentFetcher";
import { StyleGroupSelector } from "./StyleGroupSelector";
import { StyleMappings } from "./StyleMappings";
const STEP_COUNT = 3;
const HeaderRoot = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "var(--j-sp-2)",
    outline: "none"
});
const STEP_COPY = [
    {
        title: "workflows.TemplateLibrary.chooseStyleGroup",
        lede: "workflows.StyleConverter.chooseGroupLede"
    },
    {
        title: "workflows.TemplateLibrary.matchFontTypes",
        lede: "workflows.StyleConverter.selectMappingsInstructions"
    },
    null
];
export function StyleConverter({ onExit }) {
    const { t } = useTranslation();
    const [selectedGroupId, _setSelectedGroupId] = useState(null);
    const [activeStep, _setActiveStep] = useState(0);
    const [lastStepState, setLastStepState] = useState("idle");
    const headerRef = useRef(null);
    const titleRef = useRef(null);
    const shownStep = useRef(activeStep);
    useEffect(()=>{
        if (shownStep.current === activeStep) return;
        shownStep.current = activeStep;
        (titleRef.current ?? headerRef.current)?.focus();
    }, [
        activeStep
    ]);
    const decrementActiveStep = useCallback((step)=>{
        if (step !== undefined) {
            if (step < 0 || STEP_COUNT - 1 < step) return;
            if (step >= activeStep) return;
        }
        step = step ?? activeStep - 1;
        if (step < STEP_COUNT - 1) {
            setLastStepState("idle");
        }
        if (step < 1) {
            _setSelectedGroupId(null);
        }
        _setActiveStep(step);
    }, [
        activeStep
    ]);
    const incrementActiveStep = useCallback(()=>{
        _setActiveStep((prev)=>Math.min(prev + 1, STEP_COUNT - 1));
    }, []);
    const setSelectedGroupId = useCallback((groupId)=>{
        _setSelectedGroupId(groupId);
        if (groupId === null) {
            decrementActiveStep(0);
        } else {
            incrementActiveStep();
        }
    }, [
        decrementActiveStep,
        incrementActiveStep
    ]);
    const copy = STEP_COPY[activeStep];
    const header = /*#__PURE__*/ React.createElement(HeaderRoot, {
        ref: headerRef,
        tabIndex: -1
    }, /*#__PURE__*/ React.createElement(StepsBar, {
        steps: STEP_COUNT,
        current: activeStep + 1,
        label: t("workflows.StyleConverter.title")
    }), /*#__PURE__*/ React.createElement(Meta, null, t("workflows.StyleConverter.stepOf", {
        step: activeStep + 1,
        total: STEP_COUNT
    })), copy && /*#__PURE__*/ React.createElement(PageTitle, {
        titleRef: titleRef,
        title: t(copy.title),
        lede: t(copy.lede)
    }));
    const backLabels = [
        undefined,
        t("workflows.StyleConverter.fontGroup"),
        t("workflows.TemplateLibrary.matchFontTypes")
    ];
    return /*#__PURE__*/ React.createElement(WorkflowWrapper, {
        onBack: activeStep > 0 ? ()=>decrementActiveStep() : onExit,
        backLabel: backLabels[activeStep]
    }, activeStep === 0 && /*#__PURE__*/ React.createElement(StyleGroupSelector, {
        header: header,
        setSelectedGroupId: setSelectedGroupId
    }), activeStep === 1 && selectedGroupId && /*#__PURE__*/ React.createElement(StyleMappings, {
        header: header,
        selectedGroupId: selectedGroupId,
        onFormatDocument: incrementActiveStep
    }), activeStep === 2 && selectedGroupId && /*#__PURE__*/ React.createElement(FormattedDocumentFetcher, {
        header: header,
        styleGroupId: selectedGroupId,
        lastStepState: lastStepState,
        setLastStepState: setLastStepState,
        decrementActiveStep: decrementActiveStep
    }));
}
